import { Box, Stack, Typography } from '@mui/material';
import React, { ReactElement, useMemo } from 'react';
import { useIntl } from 'react-intl';

/**
 * InteractiveListPreview — Componente reusável de prévia visual de lista interativa.
 *
 * Introduzido em 2026-10-13 para a caixa InteractiveList, deliberadamente componentizado
 * pra futuras caixas que precisem renderizar prévia similar (ex.: InteractiveMenu, Carousel,
 * Lista de botões rápidos). Mentalidade sênior: componentize sempre que puder.
 *
 * Entradas:
 *   - promptMessage: texto que aparece ANTES da lista (ex.: "Qual chamado deseja visualizar?")
 *   - itemLabelTemplate: template de rotulagem por item (ex.: "{{index}}. #{{id}} - {{title}}")
 *   - mockItems: array de items mock pra renderizar. Default inclui 3 items genéricos
 *     com { id, title, name, description, code } — cobre os campos mais comuns.
 *
 * Comportamento:
 *   - Aplica o template substituindo {{index}} (1-based) e {{<campo>}} (dot-notation).
 *   - Campo ausente no item vira string vazia (não exibe "undefined").
 *   - Array vazio mostra placeholder didático.
 *
 * Deliberadamente NÃO toca em cell/attrs/persist — é apresentação pura. Caller passa
 * valores atuais, componente só renderiza.
 */

interface Props {
    promptMessage: string;
    itemLabelTemplate: string;
    /** Items usados pra montar a prévia. Se omitido, usa um default com 3 items genéricos. */
    mockItems?: Record<string, any>[];
    /** Rótulo do bloco de prévia (default = "Prévia (como aparece no chat)"). */
    headerLabel?: string;
}

// Items default — cobertura dos campos mais comuns (id, title, name, description, code).
// Ao editar, cuidar pra manter 3 items (visualmente lê melhor que 1 ou 2).
const DEFAULT_MOCK_ITEMS: Record<string, any>[] = [
    { id: 42, title: 'Problema no login', name: 'Problema no login', description: 'Não consigo acessar', code: 'LGN-001' },
    { id: 43, title: 'Dúvida sobre fatura', name: 'Dúvida sobre fatura', description: 'Qual data vence?', code: 'FIN-012' },
    { id: 44, title: 'Acesso remoto', name: 'Acesso remoto', description: 'Pedido VPN', code: 'ACC-033' },
];

/** Resolve dot-notation sobre um objeto. "customer.name" sobre {customer:{name:"X"}} → "X". */
const resolvePath = (obj: Record<string, any>, path: string): any => {
    if (!path) return undefined;
    const parts = path.split('.');
    let cur: any = obj;
    for (const p of parts) {
        if (cur == null || typeof cur !== 'object') return undefined;
        cur = cur[p];
    }
    return cur;
};

/** Aplica o template em UM item. */
export const applyItemTemplate = (template: string, item: Record<string, any>, index1Based: number): string => {
    if (!template) return `${index1Based}.`;
    return template.replace(/\{\{\s*([^{}\s]+)\s*\}\}/g, (_, key) => {
        if (key === 'index') return String(index1Based);
        const val = resolvePath(item, key);
        return val == null ? '' : String(val);
    });
};

const InteractiveListPreview = ({ promptMessage, itemLabelTemplate, mockItems, headerLabel }: Props): ReactElement => {
    const intl = useIntl();
    const items = mockItems && mockItems.length > 0 ? mockItems : DEFAULT_MOCK_ITEMS;

    const rendered = useMemo(() => (
        items.map((item, i) => applyItemTemplate(itemLabelTemplate, item, i + 1))
    ), [items, itemLabelTemplate]);

    const resolvedHeader = headerLabel ?? intl.formatMessage({
        id: 'il.preview.header',
        defaultMessage: 'Prévia (como aparece no chat):',
    });

    return (
        <Box>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
                🧪 {resolvedHeader}
            </Typography>

            {/* Bubble estilo mensagem do chat */}
            <Box sx={{
                background: '#E8F5E9',
                border: '1px solid #C8E6C9',
                borderRadius: '12px 12px 12px 2px',
                p: 1.5,
                maxWidth: '92%',
                fontSize: 13,
                lineHeight: 1.5,
            }}>
                {promptMessage && (
                    <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                        {promptMessage}
                    </Typography>
                )}

                <Stack spacing={0.25}>
                    {rendered.length > 0
                        ? rendered.map((line, i) => (
                            <Typography key={i} variant="body2" sx={{ fontFamily: 'inherit' }}>
                                {line || <em style={{ color: '#888' }}>
                                    {intl.formatMessage({
                                        id: 'il.preview.emptyLine',
                                        defaultMessage: '(linha vazia — verifique o template)',
                                    })}
                                </em>}
                            </Typography>
                        ))
                        : (
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                {intl.formatMessage({
                                    id: 'il.preview.empty',
                                    defaultMessage: '(array vazio — nenhum item pra exibir)',
                                })}
                            </Typography>
                        )}
                </Stack>
            </Box>

            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5, fontStyle: 'italic' }}>
                {intl.formatMessage({
                    id: 'il.preview.mockNote',
                    defaultMessage: 'Prévia usa items fictícios (id/title/name/description/code) — a lista real virá da variável de origem.',
                })}
            </Typography>
        </Box>
    );
};

export default InteractiveListPreview;
